import type {
  V2RequestStep,
  V2RequestAccount,
  V2ConstrainedTargetStep,
  V2OutcomeStep,
  V2InitialStateStep,
  V2AdjustedTargetStep,
} from "./explainV2Types";

interface Model {
  primary: Record<string, number>;
  secondary: Record<string, Record<string, Record<string, number>>>;
}

/**
 * Transform a V2 model (string values) to a Model (number values).
 */
export const transformV2Model = (
  model: V2RequestAccount["model"],
): Model | null => {
  if (!model) return null;
  return {
    primary: Object.fromEntries(
      Object.entries(model.primary).map(([k, v]) => [k, parseFloat(v)]),
    ),
    secondary: Object.fromEntries(
      Object.entries(model.secondary || {}).map(([secSymbol, mapping]) => [
        secSymbol,
        Object.fromEntries(
          Object.entries(mapping).map(([priSymbol, treatments]) => [
            priSymbol,
            Object.fromEntries(
              Object.entries(treatments).map(([t, w]) => [t, parseFloat(w)]),
            ),
          ]),
        ),
      ]),
    ),
  };
};

interface ToleranceExplains {
  initial_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
}

interface SpendCashExplains {
  initial_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
}

interface RaiseCashExplains {
  initial_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
}

interface AccountSettings {
  account_type: string;
  tax_type: "tax_exempt" | "taxable";
  gains_budget_lt: number | null;
  gains_budget_st: number | null;
  wash_sale_treatment: "avoid_wash_sales";
  lot_selection_algorithm?: "HIFO" | "MTAX";
}

interface Account {
  account_id: number;
  bridge_account_id: number;
  account_name: string;
  rebalance_type: "tolerance";
  is_rebalance_account: boolean;
  is_taxable?: boolean;
  raise_spend_cash_amount: number | null;
  settings: AccountSettings;
  model_id: number;
  model_name?: string;
  model: Model | null;
  security_restrictions: Record<string, unknown>;
  cash_settings: {
    target_cash_type: "absolute";
    target_cash_weight: number | null;
    target_cash_dollar: number | null;
    minimum_cash_trigger_weight: number | null;
    minimum_cash_trigger_dollar: number | null;
    minimum_cash_trigger_condition: "AND";
    maximum_cash_trigger_weight: number | null;
    maximum_cash_trigger_dollar: number | null;
    maximum_cash_trigger_condition: "AND";
  };
  balance: number | null;
  cash_balance: number | null;
  holdings: null;
  final_allocation: Record<string, number>;
  riskAllocation?: {
    current: Record<string, number>;
    target: Record<string, number>;
    postRebalance: Record<string, number>;
  };
}

export interface SharedRebalanceData {
  accounts: Record<string, Account>;
  householdModel: Model | null;
  constrainedAccounts?: Record<
    string,
    {
      constrained_target: Record<string, number>;
      securities_map: Record<
        string,
        {
          target: number;
          constrained: number;
          risk: string;
          category: string;
          adjusted_target?: number;
          adjusted_allocation?: number;
        }
      >;
      tolerance_explains?: ToleranceExplains;
      spend_cash_explains?: SpendCashExplains;
      raise_cash_explains?: RaiseCashExplains;
    }
  >;
  trades?: Record<
    string,
    Record<
      string,
      {
        shares: number;
        price: number;
        current_price?: number;
        realized_gain_loss_st: number;
        realized_gain_loss_lt: number;
        trade_weight: number | null;
        remaining_trade_value: number | null;
        remaining_shares_available_to_sell: number | null;
        tax_loss_harvest_flag?: boolean;
      }
    >
  >;
}

interface ExplainStep {
  step_name?: string; // Manual rebalance format
  stepName?: string; // Household explain format
  explain_data?: any; // Manual rebalance format
  explainData?: any; // Household explain format
}

/**
 * Transform V2 request step accounts to SharedRebalanceData accounts format
 */
const transformV2RequestToAccounts = (
  requestAccounts: V2RequestStep["accounts"],
): Record<string, Account> => {
  const accounts: Record<string, Account> = {};

  Object.entries(requestAccounts).forEach(([accountId, v2Account]) => {
    // Note: V2 explains no longer include account_name (PII removed)
    // account_name should be joined from rebalance.accounts using account_id
    accounts[accountId] = {
      account_id: v2Account.account_id,
      bridge_account_id: 0, // V2 doesn't include this
      account_name: `Account ${v2Account.account_id}`, // Placeholder - get real name from rebalance context
      rebalance_type: "tolerance",
      is_rebalance_account: true,
      is_taxable: v2Account.is_taxable,
      raise_spend_cash_amount: null,
      settings: {
        account_type: v2Account.account_type || "",
        tax_type: v2Account.is_taxable ? "taxable" : "tax_exempt",
        gains_budget_lt: null,
        gains_budget_st: null,
        wash_sale_treatment: "avoid_wash_sales",
      },
      model_id: v2Account.model_id ?? 0,
      model_name: v2Account.model_name,
      model: transformV2Model(v2Account.model),
      security_restrictions: {},
      cash_settings: {
        target_cash_type: "absolute",
        target_cash_weight: null,
        target_cash_dollar: null,
        minimum_cash_trigger_weight: null,
        minimum_cash_trigger_dollar: null,
        minimum_cash_trigger_condition: "AND",
        maximum_cash_trigger_weight: null,
        maximum_cash_trigger_dollar: null,
        maximum_cash_trigger_condition: "AND",
      },
      balance: v2Account.balance != null ? parseFloat(v2Account.balance) : null,
      cash_balance:
        v2Account.cash_balance != null
          ? parseFloat(v2Account.cash_balance)
          : null,
      holdings: null,
      final_allocation: {},
    };
  });

  return accounts;
};

/**
 * Transform V2 household model (flat string record) to Model format
 */
const transformV2HouseholdModel = (
  householdModel: Record<string, string>,
): Model => {
  const primary: Record<string, number> = {};

  Object.entries(householdModel || {}).forEach(([symbol, weight]) => {
    primary[symbol] = parseFloat(weight);
  });

  return {
    primary,
    secondary: {},
  };
};

/**
 * Transform V2 constrained_target step to constrainedAccounts format
 */
const transformV2ConstrainedTarget = (
  constrainedTarget: V2ConstrainedTargetStep["accounts"],
): SharedRebalanceData["constrainedAccounts"] => {
  const constrainedAccounts: SharedRebalanceData["constrainedAccounts"] = {};

  Object.entries(constrainedTarget).forEach(([accountId, v2Account]) => {
    // Convert string values to numbers for constrained_target
    const constrainedTargetNumbers: Record<string, number> = {};
    Object.entries(v2Account.final_target).forEach(([symbol, weight]) => {
      constrainedTargetNumbers[symbol] = parseFloat(weight);
    });

    // Build securities_map from final_target
    const securitiesMap: Record<
      string,
      {
        target: number;
        constrained: number;
        risk: string;
        category: string;
      }
    > = {};

    Object.entries(v2Account.final_target).forEach(([symbol, weight]) => {
      const weightNum = parseFloat(weight);
      securitiesMap[symbol] = {
        target: weightNum,
        constrained: weightNum,
        risk: "",
        category: v2Account.securities_map?.[symbol]?.category ?? "",
      };
    });

    constrainedAccounts[accountId] = {
      constrained_target: constrainedTargetNumbers,
      securities_map: securitiesMap,
    };
  });

  return constrainedAccounts;
};

/**
 * Merge V2 constrained_target tax_constraints into existing accounts
 * This extracts lot_selection_method and sets it in account settings
 */
const mergeV2TaxConstraints = (
  accounts: Record<string, Account>,
  constrainedTarget: V2ConstrainedTargetStep["accounts"],
): void => {
  Object.entries(constrainedTarget).forEach(([accountId, v2Account]) => {
    if (
      accounts[accountId] &&
      v2Account.tax_constraints?.lot_selection_method
    ) {
      const method =
        v2Account.tax_constraints.lot_selection_method.toUpperCase();
      if (method === "HIFO" || method === "MTAX") {
        accounts[accountId].settings.lot_selection_algorithm = method;
      }
    }
  });
};

/**
 * Merge V2 initial_state data into existing accounts (updates balance/cash_balance)
 */
const mergeV2InitialState = (
  accounts: Record<string, Account>,
  initialState: V2InitialStateStep,
): void => {
  Object.entries(initialState.accounts).forEach(([accountId, stateAccount]) => {
    if (accounts[accountId]) {
      accounts[accountId].balance = parseFloat(stateAccount.balance);
      accounts[accountId].cash_balance = parseFloat(stateAccount.cash_balance);
      accounts[accountId].is_taxable = stateAccount.is_taxable;
    }
  });
};

/**
 * Transform V2 outcome step to trades format
 */
const transformV2OutcomeToTrades = (
  accountOutcomes: V2OutcomeStep["account_outcomes"],
): SharedRebalanceData["trades"] => {
  const trades: SharedRebalanceData["trades"] = {};

  Object.entries(accountOutcomes).forEach(([accountId, outcome]) => {
    const accountTrades: Record<
      string,
      {
        shares: number;
        price: number;
        current_price?: number;
        realized_gain_loss_st: number;
        realized_gain_loss_lt: number;
        trade_weight: number | null;
        remaining_trade_value: number | null;
        remaining_shares_available_to_sell: number | null;
        tax_loss_harvest_flag?: boolean;
      }
    > = {};

    outcome.trades.forEach((trade) => {
      // Combine trades for the same symbol (V2 may have separate buy/sell)
      const shares = parseFloat(trade.shares);
      const signedShares = trade.side === "sell" ? -shares : shares;
      const price = trade.price ? parseFloat(trade.price) : 0;
      const value = parseFloat(trade.value);

      if (accountTrades[trade.symbol]) {
        // Add to existing trade
        accountTrades[trade.symbol].shares += signedShares;
        accountTrades[trade.symbol].realized_gain_loss_st += parseFloat(
          trade.realized_gain_loss_st,
        );
        accountTrades[trade.symbol].realized_gain_loss_lt += parseFloat(
          trade.realized_gain_loss_lt,
        );
        if (trade.tax_loss_harvest_flag) {
          accountTrades[trade.symbol].tax_loss_harvest_flag = true;
        }
      } else {
        accountTrades[trade.symbol] = {
          shares: signedShares,
          price: price || (shares !== 0 ? value / shares : 0),
          current_price: trade.current_price
            ? parseFloat(trade.current_price)
            : undefined,
          realized_gain_loss_st: parseFloat(trade.realized_gain_loss_st),
          realized_gain_loss_lt: parseFloat(trade.realized_gain_loss_lt),
          trade_weight: null,
          remaining_trade_value: null,
          remaining_shares_available_to_sell: null,
          tax_loss_harvest_flag: trade.tax_loss_harvest_flag,
        };
      }
    });

    trades[accountId] = accountTrades;
  });

  return trades;
};

export const createSharedRebalanceData = (
  explainSteps: ExplainStep[],
): SharedRebalanceData => {
  const sharedData: SharedRebalanceData = {
    accounts: {},
    householdModel: null,
    constrainedAccounts: {},
    trades: {},
  };

  // Store adjusted_target data to merge after all steps are processed
  let adjustedTargetData: V2AdjustedTargetStep["adjusted_target"] | null = null;

  explainSteps?.forEach((step) => {
    // Handle both step_name (manual) and stepName (household) formats
    const stepName = step.step_name || step.stepName;
    const explainData = step.explain_data || step.explainData;

    switch (stepName) {
      // V2 step: request (replaces V1 inputs)
      case "request":
        if (explainData.request) {
          sharedData.accounts = transformV2RequestToAccounts(
            explainData.request.accounts,
          );
          sharedData.householdModel = transformV2HouseholdModel(
            explainData.request.household_model,
          );
        }
        break;

      // V2 step: initial_state (contains actual account balances)
      case "initial_state":
        if (explainData.initial_state?.accounts) {
          mergeV2InitialState(sharedData.accounts, explainData.initial_state);
        }
        break;

      // V2 step: adjusted_target (contains model target after cash adjustments)
      case "adjusted_target":
        if (explainData.adjusted_target?.adjusted_target) {
          adjustedTargetData = explainData.adjusted_target.adjusted_target;
        }
        break;

      // V2 step: constrained_target (replaces V1 constrained_accounts)
      case "constrained_target":
        if (explainData.constrained_target?.accounts) {
          sharedData.constrainedAccounts = transformV2ConstrainedTarget(
            explainData.constrained_target.accounts,
          );
          // Extract lot_selection_method from tax_constraints and set in account settings
          mergeV2TaxConstraints(
            sharedData.accounts,
            explainData.constrained_target.accounts,
          );
        }
        break;

      // V2 step: outcome (replaces V1 final_taxable_target_portfolio)
      case "outcome":
        if (explainData.outcome_v2?.account_outcomes) {
          sharedData.trades = transformV2OutcomeToTrades(
            explainData.outcome_v2.account_outcomes,
          );
        }
        break;

      default:
        break;
    }
  });

  // Merge adjusted_target data into constrainedAccounts securities_map
  if (adjustedTargetData && sharedData.constrainedAccounts) {
    const typedAdjustedTarget = adjustedTargetData as Record<
      string,
      Record<string, string>
    >;
    Object.entries(typedAdjustedTarget).forEach(([accountId, symbols]) => {
      if (sharedData.constrainedAccounts?.[accountId]?.securities_map) {
        Object.entries(symbols).forEach(([symbol, weight]) => {
          const securityMap =
            sharedData.constrainedAccounts![accountId].securities_map[symbol];
          if (securityMap) {
            securityMap.adjusted_allocation = parseFloat(weight);
          }
        });
      }
    });
  }

  return sharedData;
};
